import React, { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import './FAQ.css';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      id: 1,
      question: 'What services do you offer?',
      answer: 'We offer a comprehensive range of mental health services including individual counseling, group support sessions, educational workshops, youth programs, wellness activities, and a resource center with curated materials for mental health support.',
    },
    {
      id: 2,
      question: 'How do I get started with counseling services?',
      answer: 'Getting started is easy! You can contact us through our contact form, call us directly, or send an email. We\'ll schedule an initial consultation to understand your needs and match you with the right counselor or program.',
    },
    {
      id: 3,
      question: 'Are your services confidential?',
      answer: 'Absolutely. We maintain strict confidentiality in accordance with professional ethics and legal requirements. All sessions and personal information are kept private and secure. We only share information with your explicit consent or when legally required.',
    },
    {
      id: 4,
      question: 'Do you offer services for young people?',
      answer: 'Yes! We have specialized youth programs designed specifically for teenagers and young adults. These programs address the unique mental health challenges faced by young people in a safe, supportive, and age-appropriate environment.',
    },
    {
      id: 5,
      question: 'What are your fees and do you accept insurance?',
      answer: 'We offer a sliding scale fee structure to make our services accessible to everyone. Many of our programs accept insurance, and we also provide information about financial assistance options. Contact us to discuss specific pricing and payment options.',
    },
    {
      id: 6,
      question: 'Can family members participate in the programs?',
      answer: 'Yes, we encourage family involvement when appropriate. We offer family counseling sessions and education programs that help family members better understand and support their loved ones\' mental health needs.',
    },
    {
      id: 7,
      question: 'How long does a typical program last?',
      answer: 'Program duration varies based on individual needs and the type of service. Some workshops are single sessions, while ongoing counseling can last several weeks or months. We work with you to create a personalized plan that fits your schedule and goals.',
    },
    {
      id: 8,
      question: 'Do you offer virtual/online services?',
      answer: 'Yes, we offer both in-person and virtual services. Our telehealth options provide the same quality care with the added convenience of accessing support from the comfort of your home.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="faq">
      <div className="faq-container" ref={ref}>
        <div className={`section-header ${isVisible ? 'visible' : ''}`}>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">
            Find answers to common questions about our services and programs
          </p>
        </div>

        <div className={`faq-list ${isVisible ? 'visible' : ''}`}>
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              className={`faq-item ${openIndex === index ? 'open' : ''}`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <span className="question-text">{faq.question}</span>
                <span className="question-icon">
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              <div className={`faq-answer ${openIndex === index ? 'expanded' : ''}`}>
                <div className="answer-content">
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`faq-cta ${isVisible ? 'visible' : ''}`}>
          <p>Still have questions?</p>
          <button
            className="cta-button"
            onClick={() => {
              const contactSection = document.querySelector('#contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Contact Us
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
